import { useEffect, useState, useCallback, useRef } from "react";
import { Library, Search, Star, FileText, Download, Upload, Trash2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function ResourceLibrary() {
  const { user } = useAuth();
  const { lang } = useLang();
  const isAdmin = user?.role === "admin";
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    const params = {};
    if (cat !== "all") params.category = cat;
    if (q) params.q = q;
    api.get("/resources", { params }).then(({ data }) => { setCategories(data.categories); setResources(data.resources); }).catch(() => {});
  }, [cat, q]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const toggleFav = async (r) => {
    try { const { data } = await api.post(`/resources/${r.id}/favorite`); setResources((rs) => rs.map((x) => x.id === r.id ? { ...x, is_favorite: data.is_favorite } : x)); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const download = async (r) => {
    try {
      const res = await api.get(`/resources/${r.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error(lang === "es" ? "No se pudo abrir el archivo." : "Could not open the file.");
    }
  };
  const removeRes = async (r) => {
    try { await api.delete(`/resources/${r.id}`); toast.success(lang === "es" ? "Eliminado." : "Deleted."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const upload = async () => {
    if (!file || !form.title || !form.category) { toast.error(lang === "es" ? "Completa título, categoría y archivo." : "Add title, category and a file."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("title", form.title); fd.append("description", form.description); fd.append("category", form.category);
      await api.post("/resources", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(lang === "es" ? "Recurso subido." : "Resource uploaded.");
      setUploadOpen(false); setForm({ title: "", description: "", category: "" }); setFile(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-2xl bg-accent/15 flex items-center justify-center"><Library className="h-6 w-6 text-accent" /></span>
            <h1 className="font-heading font-extrabold text-3xl tracking-tight">{lang === "es" ? "Biblioteca de Recursos" : "Resource Library"}</h1>
          </div>
          {isAdmin && (
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild><Button className="rounded-full bg-secondary hover:bg-secondary/90" data-testid="upload-resource-btn"><Plus className="h-4 w-4" /> {lang === "es" ? "Subir recurso" : "Upload resource"}</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{lang === "es" ? "Subir recurso" : "Upload resource"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-2"><Label>{lang === "es" ? "Título" : "Title"}</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="rounded-xl" data-testid="res-title" /></div>
                  <div className="space-y-2"><Label>{lang === "es" ? "Descripción" : "Description"}</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="rounded-xl resize-none" data-testid="res-desc" /></div>
                  <div className="space-y-2"><Label>{lang === "es" ? "Categoría" : "Category"}</Label>
                    <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                      <SelectTrigger className="rounded-xl" data-testid="res-category"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.txt" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="res-file-input" />
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="w-full rounded-xl border-dashed" data-testid="res-file-btn">
                    <Upload className="h-4 w-4" /> {file ? file.name : (lang === "es" ? "Elegir archivo" : "Choose file")}
                  </Button>
                </div>
                <DialogFooter><Button onClick={upload} disabled={saving} className="rounded-full bg-secondary hover:bg-secondary/90" data-testid="res-upload-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (lang === "es" ? "Subir" : "Upload")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "es" ? "Buscar" : "Search"} className="h-11 rounded-xl pl-9 w-56" data-testid="res-search" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCat("all")} className={`rounded-full px-3 py-1.5 text-sm font-medium border ${cat === "all" ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground"}`} data-testid="cat-all">{lang === "es" ? "Todas" : "All"}</button>
            {categories.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c.replace(/\W+/g, "-")}`} className={`rounded-full px-3 py-1.5 text-sm font-medium border ${cat === c ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground"}`}>{c}</button>
            ))}
          </div>
        </div>

        {resources.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground"><Library className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>{lang === "es" ? "No hay recursos todavía." : "No resources yet."}</p></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {resources.map((r) => (
              <div key={r.id} className="rounded-2xl border border-border bg-card p-5 flex flex-col" data-testid={`resource-${r.id}`}>
                <div className="flex items-start justify-between">
                  <span className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center"><FileText className="h-5 w-5 text-primary" /></span>
                  <button onClick={() => toggleFav(r)} aria-label="favorite" data-testid={`fav-res-${r.id}`}><Star className={`h-5 w-5 ${r.is_favorite ? "fill-secondary text-secondary" : "text-muted-foreground"}`} /></button>
                </div>
                <h3 className="font-heading font-bold text-lg mt-3">{r.title}</h3>
                {r.description && <p className="text-sm text-muted-foreground mt-1 flex-1">{r.description}</p>}
                <Badge variant="secondary" className="rounded-full bg-muted text-muted-foreground text-xs mt-3 w-fit">{r.category}</Badge>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => download(r)} data-testid={`download-${r.id}`}><Download className="h-4 w-4" /> {lang === "es" ? "Abrir" : "Open"}</Button>
                  {isAdmin && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeRes(r)} data-testid={`del-res-${r.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
